import { useAppDispatch, useAppSelector } from "../../hooks"
import { useNavigate, useParams } from "react-router-dom"
import {
  useGetCategoriesByTechObjMutation,
  useGetCategoriesQuery,
  useGetProjectsQuery,
  useGetServicesMutation,
  useGetTechObjectQuery,
} from "../../redux/api/main"
import { changeIsOnline, selectAuth } from "../../redux/slice/auth"
import useWindowSize from "../../hooks/useWindowSize"
import { useEffect, useLayoutEffect, useMemo, useState } from "react"
import TitleBack from "../TitleBack/TitleBack"
import { useTranslation } from "react-i18next"
import clsx from "clsx"
import BtnObject from "../Assets/BtnObject/BtnObject"
import Search from "../Search/Search"
import IconBtn from "../Assets/IconBtn/IconBtn"
import { QRMobIcon } from "../../icons/QRMobIcon"
import QrScanner from "qr-scanner"
import { detectDevice } from "../../utils/detectDevice"
import ImportantEvents from "../ImportantEvents/ImportantEvents"
import SearchList from "../SearchList/SearchList"
import { setIsSearchVal } from "../../redux/slice/params"
import { PusherPresenceChannel } from "laravel-echo/dist/channel"
import OrdersLine from "../OrdersLine/OrdersLine"
import ServicesList from "../ServicesList/ServicesList"
import { changeServiceListLoading, selectMainSlice, updateServiceList } from "../../redux/slice/main"
import OrdersLineError from "../OrdersLine/OrdersLineError"
import NoFindBtn from "../NoFindBtn/NoFindBtn"
import QrScanModal from "../Modals/QrScanModal/QrScanModal"
import InfoModal from "../Modals/InfoModal/InfoModal"
import { useScrollBlock } from "../../hooks/useScrollBlock"
import { getLocale } from "../../utils/getLocale"
import MainLayoutLoader from "../LayoutLoaders/MainLayoutLoader"
import { SESSION_ONE_PROJECT_VISITED } from "../../utils/constants"
import styles from "./MainLayout.module.scss"

const MainLayout = () => {
  const { t } = useTranslation("translation", { keyPrefix: `interface` })
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { object_id, project_id } = useParams()
  const { isDesktop } = useWindowSize()
  const { allowScroll } = useScrollBlock()
  const currentLang = getLocale()

  const { data: projectsList, isLoading: isLoadingProjects } = useGetProjectsQuery(undefined, {
    skip: !project_id || Boolean(object_id),
  })
  // получить категории услуг
  const {
    data: categoriesData,
    refetch,
    isUninitialized: isCategoriesUninitialized,
    isError: isCategoriesError,
  } = useGetCategoriesQuery(project_id || "", { skip: !!object_id || !project_id })
  const [getServices] = useGetServicesMutation()

  // получить категории по тех объекту
  const [
    getCategoriesByTechObj,
    { data: categoriesDataByTech, isUninitialized: isCategoriesTechUninitialized, isError: isErrorByTech },
  ] = useGetCategoriesByTechObjMutation()
  // получить данные по тех объекту
  const { data: techObjData, refetch: refetchTechObj } = useGetTechObjectQuery(object_id as string, {
    skip: !object_id,
  })

  const { serviceListLoading } = useAppSelector((state) => state.main)
  const { user, isAllowProposeNewServiceByProject } = useAppSelector(selectAuth)
  const { projects } = useAppSelector(selectMainSlice)

  const [searchVal, setSearchVal] = useState<string | number>("")
  const [isSearchLoading, setIsSearchLoading] = useState(false)
  const [serviceList, setServiceList] = useState<any[]>([])
  const [isQrScanModal, setQrScanModal] = useState<boolean>(false)
  const [isCamera, setCamera] = useState<boolean>(false)
  const [isErrorScaningQr, setIsErrorScaningQr] = useState(false)
  const [modalErrorText, setModalErrorText] = useState(t("errorQrScan"))
  const [errorCount, setErrorCount] = useState<number>(0)

  useLayoutEffect(() => {
    const isOneProjectVisited = sessionStorage.getItem(SESSION_ONE_PROJECT_VISITED)
    if (!projects?.length || projects.length > 1 || project_id || isOneProjectVisited) return
    if (projects?.length === 1 && !isOneProjectVisited) {
      sessionStorage.setItem(SESSION_ONE_PROJECT_VISITED, "true")
      if (object_id) {
        navigate(`/object/${object_id}/project/${projects[0].id}`)
      } else {
        navigate(`/project/${projects[0].id}`)
      }
    }
  }, [projects])

  useEffect(() => {
    dispatch(changeServiceListLoading(true))
    if (isCategoriesUninitialized && !object_id) return
    if (object_id) {
      getCategoriesByTechObj({ technical_object_id: object_id })
        .unwrap()
        .then((res) => {
          dispatch(updateServiceList(res ?? []))
          setErrorCount(0)
        })
        .catch((err: any) => {
          console.error("error in getCategoriesByTechObj:", err)
          dispatch(changeServiceListLoading(false))
          dispatch(updateServiceList([]))
        })
    } else {
      refetch()
    }
  }, [currentLang, object_id, isCategoriesUninitialized])

  useEffect(() => {
    if (isCategoriesUninitialized || !categoriesData) return
    dispatch(updateServiceList(categoriesData ?? []))
  }, [categoriesData, isCategoriesUninitialized])

  useEffect(() => {
    setIsSearchLoading(true)
    if (searchVal === "") setIsSearchLoading(false)

    dispatch(setIsSearchVal(Boolean(searchVal && searchVal !== "")))

    if (typeof searchVal === "string" && searchVal.trim().length > 0) {
      const timeoutId = setTimeout(() => {
        getServices({
          keywords: searchVal,
          technicalObjects: object_id ? [object_id] : undefined,
          project_id: project_id || "",
        })
          .unwrap()
          .then((res) => {
            setIsSearchLoading(false)
            setServiceList(res.data.aItems)
          })
          .catch((err) => console.warn(err))
      }, 500)

      return () => clearTimeout(timeoutId)
    }
  }, [searchVal, project_id])

  useEffect(() => {
    if (isDesktop) {
      QrScanner.hasCamera().then((res) => setCamera(res))
      // Html5Qrcode.getCameras()
      //   .then((devices) => {
      //     if (devices && devices.length) {
      //       setCamera(true)
      //     }
      //   })
      //   .catch((err) => {
      //     console.error("err getCameras: ", err)
      //   })
      return
    }
    const isMobile = detectDevice()
    if (isMobile) setCamera(true)
  }, [isDesktop])

  // если обрыв соединения, то через 2 секунды снова отправляем запросы, а потом уже сообщение о том, что ошибка при получении данных
  useEffect(() => {
    if (!object_id || !isErrorByTech || isCategoriesTechUninitialized) return
    dispatch(changeServiceListLoading(true))
    setErrorCount((prev) => prev + 1)
  }, [object_id, isErrorByTech, isCategoriesTechUninitialized])

  useEffect(() => {
    if (object_id || !isCategoriesError || isCategoriesUninitialized) return
    dispatch(changeServiceListLoading(true))
    setErrorCount((prev) => prev + 1)
  }, [object_id, isCategoriesError, isCategoriesUninitialized])

  useEffect(() => {
    if (!errorCount || errorCount > 3) {
      setErrorCount(0)
      return
    }
    if (object_id) {
      setTimeout(() => {
        getCategoriesByTechObj({ technical_object_id: object_id })
          .unwrap()
          .then((res) => {
            dispatch(updateServiceList(res ?? []))
            setErrorCount(0)
            refetchTechObj()
          })
          .catch((err: any) => {
            console.error("error in getCategoriesByTechObj:", err)
            dispatch(updateServiceList([]))
          })
      }, 2_000)
    } else {
      setTimeout(() => {
        refetch().then(() => {
          setErrorCount(0)
        })
      }, 2_000)
    }
  }, [errorCount, object_id])

  const titleBackBlock = useMemo(() => {
    if (searchVal || (project_id && !object_id)) {
      return (
        <TitleBack
          title={t(project_id && !searchVal ? "projects" : "search")}
          link={object_id && `/object/${object_id}/project/${project_id}`}
          setSearchVal={setSearchVal}
          searchVal={searchVal}
          className={clsx(object_id && styles.titleObj)}
        />
      )
    } else if (object_id && !searchVal) {
      return <TitleBack title={t("mainMenu")} className={styles.titleObj} />
    } else {
      return null
    }
  }, [searchVal, project_id, object_id])

  return (!project_id && isLoadingProjects) || (project_id && serviceListLoading) ? (
    isDesktop ? (
      <MainLayoutLoader />
    ) : (
      <>
        <Search searchVal={searchVal} setSearchVal={setSearchVal} isLoading={true} />
        <OrdersLine isLoading={true} />
        <ServicesList />
      </>
    )
  ) : (
    <>
      {titleBackBlock}
      {techObjData?.id && <BtnObject txt={techObjData.name} type={"div"} mode={"white"} className={styles.techObj} />}
      <div
        className={clsx(
          styles["main-layout-search-block"],
          !isDesktop && styles["main-layout-search-block__added-padding"],
        )}
      >
        <Search searchVal={searchVal} setSearchVal={setSearchVal} />
        {isCamera && !isDesktop && (
          <IconBtn
            className={styles["main-layout-search-block__QR-btn"]}
            icon={<QRMobIcon />}
            onClick={() => setQrScanModal(true)}
          />
        )}
      </div>
      {!searchVal && !object_id && <ImportantEvents />}

      {project_id && !searchVal && (
        <h3 className={styles.titleProj}>{projects?.find((p) => p.id === project_id)?.name}</h3>
      )}

      {searchVal ? (
        <SearchList list={serviceList} isSearchLoading={isSearchLoading} searchVal={searchVal} />
      ) : (
        <>
          {user?.id && !isDesktop && <OrdersLine />}
          <ServicesList isProjects={!project_id && !object_id} />

          {/*Если услуги без объекта*/}
          {!object_id && !isCategoriesUninitialized && !serviceListLoading && !categoriesData?.length && (
            <div className={styles["empty-text"]}>
              {errorCount > 0 ? (
                <OrdersLine isLoading={true} />
              ) : isCategoriesError ? (
                <OrdersLineError />
              ) : (
                t("emptyServices")
              )}
            </div>
          )}
          {/*Если услуги по объекту*/}
          {object_id && !isCategoriesTechUninitialized && !serviceListLoading && !categoriesDataByTech?.length && (
            <div className={styles["empty-text"]}>
              {errorCount > 0 ? (
                <OrdersLine isLoading={true} />
              ) : isErrorByTech ? (
                <OrdersLineError />
              ) : (
                t("emptyObject")
              )}
            </div>
          )}
        </>
      )}
      {project_id && isAllowProposeNewServiceByProject && (
        <NoFindBtn
          marginBottom={!isDesktop && !!categoriesDataByTech}
          marginTopAuto={!isDesktop && !categoriesData?.length && !categoriesDataByTech?.length}
          isLoading={serviceListLoading}
        />
      )}
      {isQrScanModal && (
        <QrScanModal
          open={isQrScanModal}
          setOpen={setQrScanModal}
          callbackError={(err: any) => {
            setModalErrorText(err || t("errorQrScan"))
            setIsErrorScaningQr(true)
            allowScroll()
            setQrScanModal(false)
          }}
          afterNavigate={(obj, projectID) => {
            allowScroll(true)
            if (!obj) return
            setQrScanModal(false)
            if (projectID) {
              navigate(`/object/${obj}/project/${projectID}`)
            } else {
              navigate(`/object/${obj}`)
            }

            if (obj === object_id) {
              // если отсканировали тот же самый объект
              getCategoriesByTechObj({ technical_object_id: object_id })
                .unwrap()
                .then((res) => {
                  dispatch(updateServiceList(res ?? []))
                  setErrorCount(0)
                  refetchTechObj()
                })
                .catch((err: any) => {
                  console.error("error in getCategoriesByTechObj:", err)
                  dispatch(updateServiceList([]))
                })
            }
          }}
        />
      )}
      {isErrorScaningQr && (
        <InfoModal modalText={modalErrorText} setOpen={() => setIsErrorScaningQr(false)} open={isErrorScaningQr} />
      )}
    </>
  )
}

export default MainLayout
